/**
 * State-Migrations-Layer für automatische Datenstruktur-Updates
 * Migriert gespeicherte Zustände auf die aktuelle Version
 */

import { FRONTEND_VERSION } from '../config/version';

/**
 * Ermittelt die Version eines gespeicherten Zustands
 * @param {Object} savedState - Gespeicherter Zustand
 * @returns {string} Version des Zustands oder "0.0.0" wenn unbekannt
 */
export function getStateVersion(savedState) {
  if (!savedState) {
    return '0.0.0';
  }
  
  // Prüfe verschiedene mögliche Version-Felder
  if (savedState.version) {
    return savedState.version;
  }
  
  if (savedState.stateVersion) {
    return savedState.stateVersion;
  }
  
  // Wenn keine Version vorhanden, annehmen dass es eine alte Version ist
  return '0.0.0';
}

/**
 * Migriert einen gespeicherten Zustand auf die aktuelle Version
 * @param {Object} savedState - Gespeicherter Zustand
 * @param {string} targetVersion - Ziel-Version (Standard: aktuelle Frontend-Version)
 * @returns {Object} Migrierter Zustand
 */
export function migrateSavedState(savedState, targetVersion = FRONTEND_VERSION) {
  if (!savedState) {
    return null;
  }

  const currentVersion = getStateVersion(savedState);
  
  // Wenn bereits auf Ziel-Version, keine Migration nötig
  if (currentVersion === targetVersion) {
    return savedState;
  }

  console.log(`[StateMigration] Migriere Zustand von Version ${currentVersion} zu ${targetVersion}`);

  // Starte mit dem gespeicherten Zustand
  let migratedState = { ...savedState };

  // Führe Migrationen Schritt für Schritt durch
  // Migration von 0.0.0 (alte Version ohne Version-Feld) zu 0.1.58
  if (compareVersions(currentVersion, '0.1.58') < 0) {
    migratedState = migrateTo_0_1_58(migratedState);
  }

  // Migration von 0.1.58 zu 0.1.59 (Beispiel für zukünftige Migrationen)
  if (compareVersions(currentVersion, '0.1.59') < 0 && compareVersions(targetVersion, '0.1.59') >= 0) {
    migratedState = migrateTo_0_1_59(migratedState);
  }

  // Migration von 0.1.59 zu 0.1.60 (Erweiterte Update-Resume-Funktionalität)
  if (compareVersions(currentVersion, '0.1.60') < 0 && compareVersions(targetVersion, '0.1.60') >= 0) {
    migratedState = migrateTo_0_1_60(migratedState);
  }

  // Setze Version auf Ziel-Version
  migratedState.version = targetVersion;
  migratedState.migratedAt = new Date().toISOString();

  console.log('[StateMigration] Migration abgeschlossen');
  return migratedState;
}

/**
 * Migration zu Version 0.1.58
 * Fügt fehlende Felder mit Default-Werten hinzu
 */
function migrateTo_0_1_58(state) {
  const migrated = { ...state };

  // Stelle sicher, dass alle erforderlichen Felder vorhanden sind
  if (!migrated.isRunning) {
    migrated.isRunning = false;
  }

  if (migrated.startTime === undefined || migrated.startTime === null) {
    migrated.startTime = null;
  }

  if (migrated.currentTime === undefined || migrated.currentTime === null) {
    migrated.currentTime = 0;
  }

  if (!migrated.measurements) {
    migrated.measurements = [];
  }

  // Migriere measurements-Array: Stelle sicher, dass alle Felder vorhanden sind
  migrated.measurements = migrated.measurements.map(m => ({
    ...m,
    vorgabeTime: m.vorgabeTime ?? (m.vorgabe_time ?? 0),
    kuerzel: m.kuerzel ?? null,
    einflussgroessen: m.einflussgroessen ?? null,
    excluded: m.excluded ?? false,
    isNote: m.isNote ?? (m.is_note ?? false),
    resumeNumber: m.resumeNumber ?? (m.resume_number ?? 1),
  }));

  if (migrated.resumeNumber === undefined || migrated.resumeNumber === null) {
    // Versuche resumeNumber aus measurements zu ermitteln
    const maxResumeNumber = migrated.measurements.length > 0
      ? Math.max(...migrated.measurements.map(m => m.resumeNumber || 1))
      : 0;
    migrated.resumeNumber = maxResumeNumber;
  }

  if (!migrated.selectedSession) {
    migrated.selectedSession = null;
  }

  if (!migrated.vorderseiteData) {
    migrated.vorderseiteData = null;
  }

  if (migrated.lastMeasurementTime === undefined || migrated.lastMeasurementTime === null) {
    migrated.lastMeasurementTime = 0;
  }

  return migrated;
}

/**
 * Migration zu Version 0.1.59 (Beispiel für zukünftige Migrationen)
 * Diese Funktion kann erweitert werden, wenn neue Felder hinzugefügt werden
 */
function migrateTo_0_1_59(state) {
  const migrated = { ...migrateTo_0_1_58(state) };

  // Beispiel: Neues Feld hinzufügen
  // if (!migrated.newField) {
  //   migrated.newField = 'defaultValue';
  // }

  return migrated;
}

/**
 * Migration zu Version 0.1.60
 * Fügt neue Felder für erweiterte Update-Resume-Funktionalität hinzu:
 * - selectedKuerzel
 * - einflussgroessenValues
 * - merke1Values
 * - scrollPositions
 * - focusInfo
 */
function migrateTo_0_1_60(state) {
  const migrated = { ...migrateTo_0_1_59(state) };

  // Neue Felder für erweiterte Update-Resume-Funktionalität
  if (migrated.selectedKuerzel === undefined) {
    migrated.selectedKuerzel = null;
  }
  if (migrated.einflussgroessenValues === undefined) {
    migrated.einflussgroessenValues = {};
  }
  if (migrated.merke1Values === undefined) {
    migrated.merke1Values = null;
  }
  if (migrated.scrollPositions === undefined) {
    migrated.scrollPositions = null;
  }
  if (migrated.focusInfo === undefined) {
    migrated.focusInfo = null;
  }
  if (migrated.focusedInput === undefined) {
    migrated.focusedInput = null;
  }

  return migrated;
}

/**
 * Vergleicht zwei semantische Versionen
 * @param {string} v1 - Erste Version
 * @param {string} v2 - Zweite Version
 * @returns {number} -1 wenn v1 < v2, 0 wenn v1 == v2, 1 wenn v1 > v2
 */
function compareVersions(v1, v2) {
  const parseVersion = (v) => {
    const parts = v.trim().replace(/^v/, '').split('.').map(Number);
    return {
      major: parts[0] || 0,
      minor: parts[1] || 0,
      patch: parts[2] || 0
    };
  };

  const ver1 = parseVersion(v1);
  const ver2 = parseVersion(v2);

  if (ver1.major !== ver2.major) {
    return ver1.major < ver2.major ? -1 : 1;
  }
  if (ver1.minor !== ver2.minor) {
    return ver1.minor < ver2.minor ? -1 : 1;
  }
  if (ver1.patch !== ver2.patch) {
    return ver1.patch < ver2.patch ? -1 : 1;
  }
  return 0;
}

/**
 * Validiert einen migrierten Zustand
 * @param {Object} state - Zu validierender Zustand
 * @returns {Object} { valid: boolean, errors: string[] }
 */
export function validateMigratedState(state) {
  const errors = [];

  if (!state) {
    return { valid: false, errors: ['Zustand ist null oder undefined'] };
  }

  // Prüfe erforderliche Felder
  if (typeof state.isRunning !== 'boolean') {
    errors.push('isRunning muss ein Boolean sein');
  }

  if (state.startTime !== null && typeof state.startTime !== 'number') {
    errors.push('startTime muss eine Zahl oder null sein');
  }

  if (typeof state.currentTime !== 'number') {
    errors.push('currentTime muss eine Zahl sein');
  }

  if (!Array.isArray(state.measurements)) {
    errors.push('measurements muss ein Array sein');
  }

  if (typeof state.resumeNumber !== 'number') {
    errors.push('resumeNumber muss eine Zahl sein');
  }

  return {
    valid: errors.length === 0,
    errors
  };
}
